import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ChevronLeft,
    Droplets,
    Truck,
    AlertTriangle,
    TrendingUp,
    TrendingDown,
    Settings,
    Activity,
    Thermometer,
    Gauge,
    Bell,
    BellOff,
    RefreshCw,
    Download,
    Calendar,
    Clock,
    MapPin,
    Filter,
    BarChart3,
    LineChart,
    PieChart,
    History,
    Zap,
    AlertCircle,
    CheckCircle,
    XCircle,
    Timer,
    Waves,
    Flame,
    Wind,
    CloudRain,
    Sun,
    Moon,
    Eye,
    Edit,
    Share2,
    MoreVertical,
    Plus,
    Minus,
    ArrowUpRight,
    ArrowDownRight,
    Target,
    Percent,
    DollarSign,
    FileText,
    Phone,
    Mail,
    Navigation
} from 'lucide-react';
import { toast } from 'sonner';

interface TankMonitorProps {
    well: any;
    onClose: () => void;
    onCreateTicket?: (well: any) => void;
    isDarkMode?: boolean;
}

interface TankData {
    id: string;
    type: 'oil' | 'water' | 'gas' | 'condensate';
    name: string;
    capacity: number;
    level: number;
    previousLevel: number;
    temp: number;
    pressure: number;
    fillRate: number;
    lastHaul?: string;
    nextScheduledHaul?: string;
    status: 'normal' | 'warning' | 'critical' | 'maintenance';
    gaugeType: 'manual' | 'auto' | 'wireless';
    lastReading?: string;
    apiGravity?: number;
    bsw?: number;
    vendor?: string;
}

interface HaulHistory {
    id: string;
    tankId: string;
    date: string;
    volume: number;
    vendor: string;
    ticketNumber: string;
    driver: string;
    status: 'completed' | 'pending' | 'scheduled';
}

interface TankAlert {
    id: string;
    tankId: string;
    type: 'high_level' | 'low_level' | 'temperature' | 'pressure' | 'leak' | 'maintenance';
    message: string;
    severity: 'info' | 'warning' | 'critical';
    timestamp: string;
    acknowledged: boolean;
}

export default function TankMonitor({ well, onClose, onCreateTicket, isDarkMode = true }: TankMonitorProps) {
    // State Management
    const [tanks, setTanks] = useState<TankData[]>([]);
    const [activeTab, setActiveTab] = useState<'overview' | 'analysis' | 'history' | 'alerts'>('overview');
    const [predictiveMode, setPredictiveMode] = useState(true);
    const [haulHistory, setHaulHistory] = useState<HaulHistory[]>([]);
    const [alerts, setAlerts] = useState<TankAlert[]>([]);
    const [selectedTank, setSelectedTank] = useState<string | null>(null);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [notificationsEnabled, setNotificationsEnabled] = useState(true);
    const [filterType, setFilterType] = useState<'all' | 'oil' | 'water'>('all');
    const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d' | '90d'>('7d');
    const [showTankDetails, setShowTankDetails] = useState<TankData | null>(null);

    // Initialize mock data
    useEffect(() => {
        const oilCount = typeof well.oilTanks === 'number' ? well.oilTanks : 2;
        const waterCount = typeof well.waterTanks === 'number' ? well.waterTanks : 1;

        const newTanks: TankData[] = [
            ...Array.from({ length: oilCount }).map((_, i) => ({
                id: `oil-${i}`,
                type: 'oil' as const,
                name: `Oil Tank ${i + 1}`,
                capacity: 500,
                level: Math.floor(Math.random() * 400) + 50,
                previousLevel: Math.floor(Math.random() * 350) + 50,
                temp: 85 + Math.random() * 15,
                pressure: 0.5 + Math.random() * 2,
                fillRate: 8 + Math.random() * 4,
                lastHaul: '2 days ago',
                nextScheduledHaul: 'Tomorrow 8:00 AM',
                status: i === 0 ? 'warning' as const : 'normal' as const,
                gaugeType: 'wireless' as const,
                lastReading: '5 min ago',
                apiGravity: 38 + Math.random() * 6,
                bsw: Math.random() * 3,
                vendor: 'Elite Oil Hauling'
            })),
            ...Array.from({ length: waterCount }).map((_, i) => ({
                id: `water-${i}`,
                type: 'water' as const,
                name: `Water Tank ${i + 1}`,
                capacity: 400,
                level: Math.floor(Math.random() * 300) + 50,
                previousLevel: Math.floor(Math.random() * 250) + 50,
                temp: 75 + Math.random() * 10,
                pressure: 0.2 + Math.random() * 1,
                fillRate: 15 + Math.random() * 5,
                lastHaul: '1 week ago',
                nextScheduledHaul: 'Friday 10:00 AM',
                status: 'normal' as const,
                gaugeType: 'wireless' as const,
                lastReading: '5 min ago',
                vendor: 'Permian Water Disposal'
            }))
        ];

        // Add a critical tank for demo
        if (newTanks.length > 0) {
            const criticalIndex = Math.min(1, newTanks.length - 1);
            newTanks[criticalIndex].level = Math.floor(newTanks[criticalIndex].capacity * 0.92);
            newTanks[criticalIndex].status = 'critical';
        }

        setTanks(newTanks);

        // Mock haul history
        setHaulHistory([
            { id: '1', tankId: 'oil-0', date: '2024-12-03 14:30', volume: 420, vendor: 'Elite Oil Hauling', ticketNumber: 'HT-2024-1234', driver: 'John Smith', status: 'completed' },
            { id: '2', tankId: 'oil-1', date: '2024-12-02 09:15', volume: 385, vendor: 'Elite Oil Hauling', ticketNumber: 'HT-2024-1233', driver: 'Mike Johnson', status: 'completed' },
            { id: '3', tankId: 'water-0', date: '2024-11-28 16:00', volume: 350, vendor: 'Permian Water Disposal', ticketNumber: 'WT-2024-0891', driver: 'Bob Wilson', status: 'completed' },
            { id: '4', tankId: 'oil-0', date: '2024-12-06 08:00', volume: 450, vendor: 'Elite Oil Hauling', ticketNumber: 'HT-2024-1235', driver: 'TBD', status: 'scheduled' },
            { id: '5', tankId: 'water-0', date: '2024-12-08 10:00', volume: 380, vendor: 'Permian Water Disposal', ticketNumber: 'WT-2024-0892', driver: 'TBD', status: 'scheduled' },
        ]);

        // Mock alerts
        setAlerts([
            { id: '1', tankId: 'oil-0', type: 'high_level', message: 'Oil Tank 1 at 92% capacity - Schedule haul immediately', severity: 'critical', timestamp: '10 min ago', acknowledged: false },
            { id: '2', tankId: 'oil-1', type: 'temperature', message: 'Oil Tank 2 temperature elevated (98°F)', severity: 'warning', timestamp: '1 hour ago', acknowledged: false },
            { id: '3', tankId: 'water-0', type: 'maintenance', message: 'Water Tank 1 gauge calibration due in 3 days', severity: 'info', timestamp: '2 hours ago', acknowledged: true },
            { id: '4', tankId: 'oil-0', type: 'pressure', message: 'Oil Tank 1 pressure within normal range', severity: 'info', timestamp: '3 hours ago', acknowledged: true },
        ]);
    }, [well]);

    // Helper functions
    const calculateTimeFull = (tank: TankData) => {
        const remaining = tank.capacity - tank.level;
        if (tank.fillRate <= 0) return 'Stable';
        const hours = remaining / tank.fillRate;
        if (hours < 1) return '< 1 hour';
        if (hours < 24) return `${Math.floor(hours)} hours`;
        return `${(hours / 24).toFixed(1)} days`;
    };

    const getTankColor = (type: string) => {
        switch (type) {
            case 'oil': return { gradient: 'from-amber-900 via-amber-700 to-amber-600', bg: 'bg-amber-500', text: 'text-amber-500' };
            case 'water': return { gradient: 'from-blue-900 via-blue-700 to-cyan-500', bg: 'bg-blue-500', text: 'text-blue-500' };
            case 'gas': return { gradient: 'from-green-900 via-green-700 to-green-500', bg: 'bg-green-500', text: 'text-green-500' };
            default: return { gradient: 'from-gray-900 via-gray-700 to-gray-500', bg: 'bg-gray-500', text: 'text-gray-500' };
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
            case 'warning': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
            case 'maintenance': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
            default: return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
        }
    };

    const getLevelPercentage = (tank: TankData) => Math.round((tank.level / tank.capacity) * 100);

    const getFillRateChange = (tank: TankData) => {
        const change = tank.level - tank.previousLevel;
        return change;
    };

    const handleAcknowledgeAlert = (alertId: string) => {
        setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, acknowledged: true } : a));
        toast.success('Alert acknowledged');
    };

    const handleScheduleHaul = (tank: TankData) => {
        if (onCreateTicket) {
            onCreateTicket(well);
            toast.success(`Haul scheduled for ${tank.name}`);
        }
    };

    const handleEmergencyHaul = (tank: TankData) => {
        toast.success(`Emergency haul requested for ${tank.name} - Dispatching now!`);
    };

    const handleRefresh = () => {
        toast.info('Refreshing tank data...');
    };

    const handleExportReport = () => {
        toast.success('Exporting tank report...');
    };

    // Stats calculations
    const totalOilVolume = tanks.filter(t => t.type === 'oil').reduce((acc, t) => acc + t.level, 0);
    const totalWaterVolume = tanks.filter(t => t.type === 'water').reduce((acc, t) => acc + t.level, 0);
    const totalFillRate = tanks.reduce((acc, t) => acc + t.fillRate, 0);
    const criticalCount = tanks.filter(t => t.status === 'critical').length;
    const warningCount = tanks.filter(t => t.status === 'warning').length;
    const unacknowledgedAlerts = alerts.filter(a => !a.acknowledged).length;

    const filteredTanks = filterType === 'all' ? tanks : tanks.filter(t => t.type === filterType);

    return (
        <div className={`fixed inset-0 z-[100] ${isDarkMode ? 'bg-slate-900' : 'bg-white'} overflow-y-auto`}>
            {/* Sticky Header with Back Button - Same as NewTicketModal */}
            <div className={`sticky top-0 z-10 flex items-center justify-between p-4 pt-[max(1rem,env(safe-area-inset-top))] border-b ${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onClose();
                    }}
                    className={`flex items-center gap-1 ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                    <ChevronLeft className="w-6 h-6" />
                    <span className="text-base font-medium">Back</span>
                </button>

                <div className="flex flex-col items-center">
                    <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Tank Monitor</h2>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{well.name}</span>
                        {criticalCount > 0 && (
                            <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                                {criticalCount} CRITICAL
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                        className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-100'} ${notificationsEnabled ? 'text-blue-500' : 'text-gray-400'}`}
                    >
                        {notificationsEnabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
                    </button>
                    <button
                        onClick={handleRefresh}
                        className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-slate-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
                    >
                        <RefreshCw className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Main Content - Added safe area padding */}
            <div className="p-4 pb-[max(6rem,calc(6rem+env(safe-area-inset-bottom)))]">
                {/* Quick Stats */}
                <div className="grid grid-cols-4 gap-2 mb-4">
                    <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                        <Droplets className={`w-4 h-4 mb-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                        <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{totalOilVolume}</div>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Oil (bbl)</div>
                    </div>
                    <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                        <Waves className={`w-4 h-4 mb-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                        <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{totalWaterVolume}</div>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Water (bbl)</div>
                    </div>
                    <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                        <TrendingUp className={`w-4 h-4 mb-1 text-emerald-500`} />
                        <div className={`text-lg font-bold text-emerald-500`}>+{totalFillRate.toFixed(0)}</div>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>bbl/hr</div>
                    </div>
                    <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                        <AlertTriangle className={`w-4 h-4 mb-1 ${unacknowledgedAlerts > 0 ? 'text-red-500' : 'text-emerald-500'}`} />
                        <div className={`text-lg font-bold ${unacknowledgedAlerts > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{unacknowledgedAlerts}</div>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Alerts</div>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className={`flex gap-1 p-1 rounded-xl mb-4 ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                    {[
                        { id: 'overview', label: 'Overview', icon: Eye },
                        { id: 'analysis', label: 'Analysis', icon: BarChart3 },
                        { id: 'history', label: 'History', icon: History },
                        { id: 'alerts', label: `Alerts${unacknowledgedAlerts > 0 ? ` (${unacknowledgedAlerts})` : ''}`, icon: Bell },
                    ].map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            onClick={() => setActiveTab(id as any)}
                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${activeTab === id
                                ? 'bg-blue-600 text-white shadow-lg'
                                : isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                                }`}
                        >
                            <Icon className="w-4 h-4" />
                            <span className="hidden sm:inline">{label}</span>
                        </button>
                    ))}
                </div>

                {/* Filter Pills (for Overview) */}
                {activeTab === 'overview' && (
                    <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
                        <Filter className={`w-4 h-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        {['all', 'oil', 'water'].map(type => (
                            <button
                                key={type}
                                onClick={() => setFilterType(type as any)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${filterType === type
                                    ? type === 'oil' ? 'bg-amber-600 text-white' : type === 'water' ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white'
                                    : isDarkMode ? 'bg-slate-800 text-gray-300 hover:bg-slate-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                            >
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                            </button>
                        ))}
                        <button
                            onClick={() => setPredictiveMode(!predictiveMode)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 ${predictiveMode
                                ? 'bg-purple-600 text-white'
                                : isDarkMode ? 'bg-slate-800 text-gray-300' : 'bg-gray-200 text-gray-700'
                                }`}
                        >
                            <Zap className="w-3 h-3" />
                            Predictive
                        </button>
                    </div>
                )}

                {/* Overview Tab */}
                <AnimatePresence mode="wait">
                    {activeTab === 'overview' && (
                        <motion.div
                            key="overview"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="space-y-4"
                        >
                            {filteredTanks.map((tank) => {
                                const levelPct = getLevelPercentage(tank);
                                const tankColor = getTankColor(tank.type);
                                const rateChange = getFillRateChange(tank);

                                return (
                                    <motion.div
                                        key={tank.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-slate-800' : 'bg-white'} border ${tank.status === 'critical' ? 'border-red-500/50 ring-1 ring-red-500/20' :
                                            tank.status === 'warning' ? 'border-yellow-500/50' :
                                                isDarkMode ? 'border-slate-700' : 'border-gray-200'
                                            }`}
                                    >
                                        <div className="p-4">
                                            {/* Tank Header */}
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center gap-3">
                                                    {/* Mini Tank Visualization */}
                                                    <div className={`w-12 h-16 rounded-lg relative overflow-hidden ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                                                        <motion.div
                                                            initial={{ height: 0 }}
                                                            animate={{ height: `${levelPct}%` }}
                                                            transition={{ duration: 1, ease: "easeOut" }}
                                                            className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t ${tankColor.gradient}`}
                                                        >
                                                            <div className="w-full h-1 bg-white/30 absolute top-0"></div>
                                                        </motion.div>
                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                            <span className="text-white text-xs font-bold drop-shadow-lg">{levelPct}%</span>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{tank.name}</h3>
                                                        <div className="flex items-center gap-2 mt-0.5">
                                                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${getStatusColor(tank.status)}`}>
                                                                {tank.status}
                                                            </span>
                                                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                                {tank.gaugeType === 'wireless' && '📡 '}{tank.lastReading}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <button className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}>
                                                    <MoreVertical className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                                                </button>
                                            </div>

                                            {/* Tank Stats Grid */}
                                            <div className="grid grid-cols-3 gap-3 mb-3">
                                                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                                                    <p className={`text-[10px] uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Volume</p>
                                                    <p className={`text-sm font-bold font-mono ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        {tank.level} / {tank.capacity}
                                                    </p>
                                                </div>
                                                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                                                    <p className={`text-[10px] uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Temperature</p>
                                                    <p className={`text-sm font-bold font-mono flex items-center gap-1 ${tank.temp > 95 ? 'text-orange-400' : isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        <Thermometer className="w-3 h-3" />
                                                        {tank.temp.toFixed(1)}°F
                                                    </p>
                                                </div>
                                                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                                                    <p className={`text-[10px] uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Fill Rate</p>
                                                    <p className={`text-sm font-bold font-mono flex items-center gap-1 ${rateChange > 0 ? 'text-emerald-400' : 'text-gray-400'}`}>
                                                        {rateChange > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                                                        {tank.fillRate.toFixed(1)}/hr
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Additional Info (Oil specific) */}
                                            {tank.type === 'oil' && tank.apiGravity && (
                                                <div className="grid grid-cols-2 gap-3 mb-3">
                                                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                                                        <p className={`text-[10px] uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>API Gravity</p>
                                                        <p className={`text-sm font-bold font-mono ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {tank.apiGravity.toFixed(1)}°
                                                        </p>
                                                    </div>
                                                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
                                                        <p className={`text-[10px] uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>BS&W</p>
                                                        <p className={`text-sm font-bold font-mono ${tank.bsw! > 2 ? 'text-yellow-400' : isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                            {tank.bsw?.toFixed(2)}%
                                                        </p>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Predictive Info */}
                                            {predictiveMode && (
                                                <div className={`p-3 rounded-lg mb-3 ${isDarkMode ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-purple-50 border border-purple-200'}`}>
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <Timer className={`w-4 h-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                                                            <span className={`text-xs font-medium ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>Time to Full</span>
                                                        </div>
                                                        <span className={`text-sm font-bold ${isDarkMode ? 'text-purple-200' : 'text-purple-800'}`}>
                                                            {calculateTimeFull(tank)}
                                                        </span>
                                                    </div>
                                                    {tank.nextScheduledHaul && (
                                                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-purple-500/20">
                                                            <div className="flex items-center gap-2">
                                                                <Truck className={`w-4 h-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                                                                <span className={`text-xs ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>Next Haul</span>
                                                            </div>
                                                            <span className={`text-xs font-medium ${isDarkMode ? 'text-purple-200' : 'text-purple-800'}`}>
                                                                {tank.nextScheduledHaul}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Action Buttons */}
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => toast.info(`Viewing history for ${tank.name}`)}
                                                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 ${isDarkMode ? 'bg-slate-700 text-gray-300 hover:bg-slate-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                        }`}
                                                >
                                                    <History className="w-3 h-3" />
                                                    History
                                                </button>
                                                <button
                                                    onClick={() => handleScheduleHaul(tank)}
                                                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 ${tank.status === 'critical'
                                                        ? 'bg-red-600 text-white hover:bg-red-500 animate-pulse'
                                                        : levelPct > 80
                                                            ? 'bg-yellow-600 text-white hover:bg-yellow-500'
                                                            : 'bg-blue-600 text-white hover:bg-blue-500'
                                                        }`}
                                                >
                                                    <Truck className="w-3 h-3" />
                                                    {tank.status === 'critical' ? 'HAUL NOW' : 'Schedule Haul'}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Vendor Info */}
                                        {tank.vendor && (
                                            <div className={`px-4 py-2 border-t flex items-center justify-between ${isDarkMode ? 'bg-slate-700/30 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    Vendor: {tank.vendor}
                                                </span>
                                                <div className="flex gap-1">
                                                    <button className={`p-1.5 rounded ${isDarkMode ? 'hover:bg-slate-600' : 'hover:bg-gray-200'}`}>
                                                        <Phone className="w-3 h-3 text-blue-500" />
                                                    </button>
                                                    <button className={`p-1.5 rounded ${isDarkMode ? 'hover:bg-slate-600' : 'hover:bg-gray-200'}`}>
                                                        <Mail className="w-3 h-3 text-blue-500" />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </motion.div>
                    )}

                    {/* Analysis Tab */}
                    {activeTab === 'analysis' && (
                        <motion.div
                            key="analysis"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="space-y-4"
                        >
                            {/* Time Range Selector */}
                            <div className="flex gap-1 overflow-x-auto pb-1">
                                {['24h', '7d', '30d', '90d'].map(range => (
                                    <button
                                        key={range}
                                        onClick={() => setTimeRange(range as any)}
                                        className={`px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap ${timeRange === range
                                            ? 'bg-blue-600 text-white'
                                            : isDarkMode ? 'bg-slate-800 text-gray-300' : 'bg-gray-200 text-gray-700'
                                            }`}
                                    >
                                        {range}
                                    </button>
                                ))}
                            </div>

                            {/* Production Summary */}
                            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                                <h3 className={`font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Production Summary</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Oil Hauled</p>
                                        <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>2,847 bbl</p>
                                        <p className="text-xs text-emerald-500 flex items-center gap-1">
                                            <ArrowUpRight className="w-3 h-3" /> +12% vs prev
                                        </p>
                                    </div>
                                    <div>
                                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Water Hauled</p>
                                        <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>1,523 bbl</p>
                                        <p className="text-xs text-emerald-500 flex items-center gap-1">
                                            <ArrowUpRight className="w-3 h-3" /> +8% vs prev
                                        </p>
                                    </div>
                                    <div>
                                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Average API</p>
                                        <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>39.2°</p>
                                    </div>
                                    <div>
                                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Haul Efficiency</p>
                                        <p className={`text-xl font-bold text-emerald-500`}>94%</p>
                                    </div>
                                </div>
                            </div>

                            {/* Chart Placeholder */}
                            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                                <h3 className={`font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Fill Rate Trends</h3>
                                <div className="h-48 flex items-center justify-center">
                                    <div className="text-center">
                                        <LineChart className={`w-16 h-16 mx-auto mb-3 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Interactive charts coming soon</p>
                                    </div>
                                </div>
                            </div>

                            {/* Cost Analysis */}
                            <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                                <h3 className={`font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Cost Analysis</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Oil Haul Revenue</span>
                                        <span className={`font-bold text-emerald-500`}>+$185,640</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Water Disposal Cost</span>
                                        <span className={`font-bold text-red-400`}>-$12,184</span>
                                    </div>
                                    <div className={`pt-2 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-300'}`}>
                                        <div className="flex justify-between items-center">
                                            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Net Revenue</span>
                                            <span className={`text-lg font-bold text-emerald-500`}>$173,456</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* History Tab */}
                    {activeTab === 'history' && (
                        <motion.div
                            key="history"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="space-y-3"
                        >
                            {haulHistory.map((haul) => (
                                <div
                                    key={haul.id}
                                    className={`p-4 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-white'} border ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${haul.status === 'completed' ? 'bg-emerald-500/20 text-emerald-500' :
                                                haul.status === 'scheduled' ? 'bg-blue-500/20 text-blue-500' :
                                                    'bg-yellow-500/20 text-yellow-500'
                                                }`}>
                                                <Truck className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                    {tanks.find(t => t.id === haul.tankId)?.name || 'Unknown Tank'}
                                                </p>
                                                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    {haul.vendor} • {haul.driver}
                                                </p>
                                            </div>
                                        </div>
                                        <span className={`px-2 py-1 rounded text-xs font-bold ${haul.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                                            haul.status === 'scheduled' ? 'bg-blue-500/20 text-blue-400' :
                                                'bg-yellow-500/20 text-yellow-400'
                                            }`}>
                                            {haul.status.toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        <div>
                                            <p className={`text-[10px] uppercase ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Date</p>
                                            <p className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{haul.date}</p>
                                        </div>
                                        <div>
                                            <p className={`text-[10px] uppercase ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Volume</p>
                                            <p className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{haul.volume} bbl</p>
                                        </div>
                                        <div>
                                            <p className={`text-[10px] uppercase ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Ticket #</p>
                                            <p className={`text-xs font-medium font-mono ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{haul.ticketNumber}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </motion.div>
                    )}

                    {/* Alerts Tab */}
                    {activeTab === 'alerts' && (
                        <motion.div
                            key="alerts"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="space-y-3"
                        >
                            {alerts.map((alert) => (
                                <div
                                    key={alert.id}
                                    className={`p-4 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-white'} border ${!alert.acknowledged ? 'ring-1 ring-blue-500/30' : ''
                                        } ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${alert.severity === 'critical' ? 'bg-red-500/20 text-red-500' :
                                            alert.severity === 'warning' ? 'bg-yellow-500/20 text-yellow-500' :
                                                'bg-blue-500/20 text-blue-500'
                                            }`}>
                                            <AlertTriangle className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex justify-between items-start">
                                                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                    {alert.message}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    {alert.timestamp}
                                                </span>
                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${alert.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                                                    alert.severity === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
                                                        'bg-blue-500/20 text-blue-400'
                                                    }`}>
                                                    {alert.severity.toUpperCase()}
                                                </span>
                                                {alert.acknowledged && (
                                                    <span className="flex items-center gap-1 text-xs text-emerald-500">
                                                        <CheckCircle className="w-3 h-3" /> Acknowledged
                                                    </span>
                                                )}
                                            </div>
                                            {!alert.acknowledged && (
                                                <button
                                                    onClick={() => handleAcknowledgeAlert(alert.id)}
                                                    className="mt-2 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-500"
                                                >
                                                    Acknowledge
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {alerts.length === 0 && (
                                <div className="text-center py-12">
                                    <CheckCircle className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-emerald-500' : 'text-emerald-400'}`} />
                                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>All Clear!</p>
                                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No active alerts</p>
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Quick Actions */}
                <div className="grid grid-cols-3 gap-3 mt-6">
                    <button
                        onClick={handleExportReport}
                        className={`p-4 rounded-xl flex flex-col items-center gap-2 ${isDarkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'}`}
                    >
                        <Download className={`w-6 h-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Export</span>
                    </button>
                    <button
                        onClick={() => setShowScheduleModal(true)}
                        className={`p-4 rounded-xl flex flex-col items-center gap-2 ${isDarkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'}`}
                    >
                        <Calendar className={`w-6 h-6 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Schedule</span>
                    </button>
                    <button
                        onClick={() => toast.info('Opening settings...')}
                        className={`p-4 rounded-xl flex flex-col items-center gap-2 ${isDarkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'}`}
                    >
                        <Settings className={`w-6 h-6 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Settings</span>
                    </button>
                </div>

                {/* Batch Haul Button */}
                <button
                    onClick={() => {
                        if (onCreateTicket) {
                            onCreateTicket(well);
                            toast.success('Batch haul request created');
                        }
                    }}
                    className="w-full mt-4 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-bold shadow-lg flex items-center justify-center gap-2 hover:from-blue-500 hover:to-blue-600 transition-all"
                >
                    <Truck className="w-5 h-5" />
                    Schedule Batch Haul
                </button>
            </div>
        </div>
    );
}
